// Vector port of PrintableBracketTree.js. `buildLayout`/`cellText` below
// are copied verbatim from there (pure geometry math, independent of the
// DOM) - only the drawing layer changes. The original renders this as an
// HTML <table> with rowSpan-merged cells; instead of replicating rowSpan
// semantics, this draws in two passes that are mathematically equivalent:
// Pass A walks (round, k) pairs and draws each round-connector vertical
// line + match-number label + winner-slot text + score text once (a
// rowSpan of `halfSpan` rows is just one line drawn `halfSpan*ROW_H` tall);
// Pass B walks every round-1 row and draws its №/seed/player-name cells
// (the only per-row, non-spanned column).
import { t } from "i18next";
import { PRINT_THEME as T } from "./printTheme";
import { getRoundTitle, getOrdinalSuffix } from "../../../constants";
import { getMatchParticipantNames, getMatchScoreText } from "./exportUtils";
import { px2pt, px2mm, lineHeightMm, drawText, hLine, vLine } from "./pdfKit";

const buildLayout = (draw) => {
    const byNum = {};
    const round1Matches = [];
    (draw.matches || []).forEach(m => {
        byNum[m.match_number] = m;
        if (m.round_number === 1) round1Matches.push(m);
    });
    round1Matches.sort((a, b) => (a.match_number || 0) - (b.match_number || 0));

    const nums = (draw.matches || []).map(m => m.match_number);
    const matchOffset = nums.length ? Math.min(...nums) - 1 : 0;

    const numberPlayers = draw.number_players || round1Matches.length * 2;
    const N = Math.pow(2, Math.ceil(Math.log2(Math.max(2, numberPlayers))));
    const R = Math.log2(N);
    const matchStart = r => matchOffset + N - (N >> (r - 1));
    const showSeedColumn = round1Matches.some(m => m.seed1 || m.seed2);

    return { byNum, round1Matches, N, R, matchStart, showSeedColumn };
};

// Real name check independent of getMatchParticipantNames' own fallback
// (which returns generic "Player 1"/"Pair 1"/"Team 1" text meant for
// flat-table exports) - the live Bracket.js instead shows "Участник #N"
// for an unfilled slot, so an empty slot needs to be detected here before
// that fallback text ever gets produced.
const hasRealName = (match, side, grade) => {
    if (grade === 3) return !!(side === 1 ? match.team_1_name : match.team_2_name);
    if (grade === 2) return !!(side === 1 ? (match.player_1_name || match.player_3_name) : (match.player_2_name || match.player_4_name));
    return !!(side === 1 ? match.player_1_name : match.player_2_name);
};

// `slotNumber` mirrors Bracket.js's own renderSide(match, side, isMainDraw,
// slotNumber): passed (the row's 1-based position) for round-1 slots,
// omitted for every later-round call - an unfilled later-round slot reads
// "Участник" with no number there, same as the live site.
const cellText = (grade) => (match, side, isFirstRound, slotNumber) => {
    const emptyLabel = () => `${t("Participant")}${slotNumber ? ` #${slotNumber}` : ""}`;
    if (!match) return { text: isFirstRound ? emptyLabel() : "", isBye: false, isWinner: false, isLoser: false };
    const isBye = side === 1 ? match.x1 : match.x2;
    if (isBye) return { text: t("Bye"), isBye: true, isWinner: false, isLoser: false };
    if (!hasRealName(match, side, grade)) return { text: emptyLabel(), isBye: false, isWinner: false, isLoser: false };

    const { side1, side2 } = getMatchParticipantNames(match, grade);
    const opponentIsBye = side === 1 ? match.x2 : match.x1;
    const isWinner = match.winner_side === side || opponentIsBye;
    const isLoser = match.winner_side != null && match.winner_side !== side;
    const seed = side === 1 ? match.seed1 : match.seed2;
    const seedPrefix = (isFirstRound && seed) ? `(${seed}) ` : "";

    return { text: `${seedPrefix}${side === 1 ? side1 : side2}`, isBye: false, isWinner, isLoser };
};

const NAME_FS = px2pt(11);
const SCORE_FS = NAME_FS * 1.1;
const NUM_FS = px2pt(10);
const MATCH_NUM_FS = px2pt(9);
const HEADER_FS = px2pt(11);
const CARD_HEADER_FS = px2pt(14);

const ROW_H = px2mm(20);
const HEADER_H = px2mm(30);
const COL_N = px2mm(22);
const COL_SEED = px2mm(24);
const COL_PLAYER = px2mm(180);
const COL_MATCHNUM = px2mm(26);
const COL_WINNER = px2mm(180);
const CARD_HEADER_H = lineHeightMm(CARD_HEADER_FS, 1.2) + px2mm(16);
const CARD_PAD = px2mm(20);
const CARD_GAP = px2mm(28);
const CARD_RADIUS = px2mm(5);

// No green in the PDF (vector export) - winner reads as black/bold instead
// of colored, loser/bye as gray. The PNG (screenshot) export keeps the
// site's real green winner color; this is PDF-only by design.
const nameColor = ({ isLoser, isBye }) => (isLoser || isBye) ? T.muted : T.ink;
const nameStyle = ({ isWinner }) => isWinner ? "bold" : "normal";

export const singleBracketWidthMm = (draw) => {
    const { R, showSeedColumn } = buildLayout(draw);
    if (!R) return 0;
    return COL_N + (showSeedColumn ? COL_SEED : 0) + COL_PLAYER + R * (COL_MATCHNUM + COL_WINNER);
};

const singleBracketHeightMm = (draw) => {
    const { N, R } = buildLayout(draw);
    if (!R) return 0;
    return HEADER_H + N * ROW_H;
};

export const bracketTreeContentWidthMm = (draws) => draws.reduce((m, d) => Math.max(m, singleBracketWidthMm(d)), 0);

export const measureBracketTree = (draws) => {
    if (!draws.length) return px2mm(40);
    return draws.reduce((sum, d, idx) => {
        const h = singleBracketHeightMm(d);
        if (!h) return sum;
        return sum + CARD_HEADER_H + CARD_PAD + h + CARD_PAD + (idx > 0 ? CARD_GAP : 0);
    }, 0);
};

const drawSingleBracket = (pdf, x, y, draw, grade, i18n, widthMm) => {
    const { byNum, round1Matches, N, R, matchStart, showSeedColumn } = buildLayout(draw);
    if (!R) return y;
    const display = cellText(grade);

    const colPlayerX = x + COL_N + (showSeedColumn ? COL_SEED : 0);
    const bodyTop = y + HEADER_H;

    // Header row
    pdf.setFont(pdf.__pdfFontName || "PTSans", "normal");
    pdf.setFontSize(HEADER_FS);
    pdf.setTextColor(T.ink);
    pdf.text("№", x + COL_N / 2, y + HEADER_H / 2, { align: "center" });
    if (showSeedColumn) pdf.text(t("S").toUpperCase(), x + COL_N + COL_SEED / 2, y + HEADER_H / 2, { align: "center" });
    pdf.text(getRoundTitle(1, R, t, i18n, null, false).toUpperCase(), colPlayerX + COL_PLAYER / 2, y + HEADER_H / 2, { align: "center" });
    for (let r = 1; r <= R; r++) {
        const rx = colPlayerX + COL_PLAYER + (r - 1) * (COL_MATCHNUM + COL_WINNER);
        const label = r < R ? getRoundTitle(r + 1, R, t, i18n, null, false) : `${draw.number}${getOrdinalSuffix(draw.number)} ${t("Place")}`;
        pdf.text(label.toUpperCase(), rx + COL_MATCHNUM + COL_WINNER / 2, y + HEADER_H / 2, { align: "center" });
    }

    // Pass B: round-1 zebra + №/seed/player cells
    for (let i = 0; i < N; i++) {
        const matchIdx = i >> 1;
        const side = i % 2 === 0 ? 1 : 2;
        const r1Match = round1Matches[matchIdx];
        const rowY = bodyTop + i * ROW_H;

        if (matchIdx % 2 === 1) {
            pdf.setFillColor(T.superWhite);
            pdf.rect(x, rowY, COL_N + (showSeedColumn ? COL_SEED : 0) + COL_PLAYER, ROW_H, "F");
        }

        pdf.setFont(pdf.__pdfFontName || "PTSans", "normal");
        pdf.setFontSize(NUM_FS);
        pdf.setTextColor(T.muted);
        pdf.text(String(i + 1), x + COL_N / 2, rowY + ROW_H / 2, { align: "center" });

        if (showSeedColumn) {
            const seed = r1Match ? (side === 1 ? r1Match.seed1 : r1Match.seed2) : "";
            pdf.text(seed ? String(seed) : "", x + COL_N + COL_SEED / 2, rowY + ROW_H / 2, { align: "center" });
        }

        const p1 = display(r1Match, side, true, i + 1);
        drawText(pdf, p1.text, colPlayerX + px2mm(8), rowY + ROW_H / 2 - lineHeightMm(NAME_FS, 1) / 2, {
            fontSizePt: NAME_FS, color: nameColor(p1), style: nameStyle(p1), maxWidthMm: COL_PLAYER - px2mm(16),
        });
    }

    // Grid hairlines under every row (n/seed/player section only - matches
    // the original's "no horizontal rules; zebra + round borders only" for
    // round columns, but td borderBottom is present for n/seed/player).
    for (let i = 0; i <= N; i++) {
        hLine(pdf, x, bodyTop + i * ROW_H, COL_N + (showSeedColumn ? COL_SEED : 0) + COL_PLAYER, T.border, px2mm(0.5));
    }

    // Pass A: per-round connectors + match-number + winner + score
    for (let r = 1; r <= R; r++) {
        const blockSize = 1 << r;
        const span = Math.max(2, 1 << (r - 1));
        const halfSpan = span >> 1;
        const rx = colPlayerX + COL_PLAYER + (r - 1) * (COL_MATCHNUM + COL_WINNER);
        const winnerX = rx + COL_MATCHNUM;
        const kCount = N >> r;

        for (let k = 0; k < kCount; k++) {
            const blockStart = k * blockSize;
            const mRow = blockStart + (blockSize >> 1) - 1;
            const matchNum = matchStart(r) + k + 1;
            const startRow = mRow - halfSpan + 1;
            const currentMatch = byNum[matchNum];

            const connectorY = bodyTop + startRow * ROW_H;
            vLine(pdf, rx, connectorY, span * ROW_H, T.border, px2mm(0.5));

            pdf.setFont(pdf.__pdfFontName || "PTSans", "normal");
            pdf.setFontSize(MATCH_NUM_FS);
            pdf.setTextColor(T.muted);
            pdf.text(`#${matchNum}`, rx + px2mm(2), bodyTop + (startRow + halfSpan) * ROW_H - px2mm(3));

            const nextMatchNum = r < R ? matchStart(r + 1) + (k >> 1) + 1 : null;
            const nextMatch = nextMatchNum ? byNum[nextMatchNum] : null;
            const nextSide = (k & 1) === 0 ? 1 : 2;
            let content = { text: `${t("Winner")} #${matchNum}`, isBye: false, isWinner: false, isLoser: false };
            if (nextMatch) content = display(nextMatch, nextSide, false);
            else if (currentMatch?.winner_side) content = display(currentMatch, currentMatch.winner_side, false);
            else if (currentMatch?.x1 && currentMatch?.x2) content = { text: t("Bye"), isBye: true };
            else if (currentMatch?.x1) content = display(currentMatch, 2, false);
            else if (currentMatch?.x2) content = display(currentMatch, 1, false);

            const winnerRowY = bodyTop + mRow * ROW_H;
            drawText(pdf, content.text, winnerX + px2mm(8), winnerRowY + ROW_H / 2 - lineHeightMm(NAME_FS, 1) / 2, {
                fontSizePt: NAME_FS, color: nameColor(content), style: nameStyle(content), maxWidthMm: COL_WINNER - px2mm(16),
            });
            hLine(pdf, winnerX, winnerRowY + ROW_H, COL_WINNER, T.border, px2mm(0.5));

            const scoreText = getMatchScoreText(currentMatch);
            if (scoreText) {
                const scoreRowY = bodyTop + (mRow + 1) * ROW_H;
                pdf.setFont(pdf.__pdfFontName || "PTSans", "normal");
                pdf.setFontSize(SCORE_FS);
                pdf.setTextColor(T.muted);
                pdf.text(scoreText, winnerX + px2mm(8), scoreRowY + px2mm(6));
            }
        }
    }

    return bodyTop + N * ROW_H;
};

export const drawBracketTree = (pdf, x, y, data, grade, i18n) => {
    const draws = Array.isArray(data) ? data : (data?.draws || []);
    const contentWidthMm = bracketTreeContentWidthMm(draws.length ? draws : [{}]);
    let cy = y;

    if (!draws.length) {
        pdf.setFillColor(T.superWhite);
        pdf.rect(x, cy, contentWidthMm || px2mm(400), px2mm(40), "F");
        drawText(pdf, t("No data available"), x + (contentWidthMm || px2mm(400)) / 2, cy + px2mm(15), { fontSizePt: px2pt(13), color: T.muted, align: "center" });
        return cy + px2mm(40);
    }

    draws.forEach((draw, idx) => {
        const h = singleBracketHeightMm(draw);
        if (!h) return;
        if (idx > 0) cy += CARD_GAP;
        const cardTop = cy;

        // Gray/white/uppercase title bar - site's own public-view equivalent
        // fills this green; kept the white-uppercase-on-filled-bar treatment
        // but in a neutral gray tone instead, per user preference.
        pdf.setFillColor(T.headerGray);
        pdf.rect(x, cy, contentWidthMm, CARD_HEADER_H, "F");
        drawText(pdf, (draw.name || `${draw.number}${getOrdinalSuffix(draw.number)} ${t("Place")}`).toUpperCase(), x + px2mm(12), cy + CARD_HEADER_H / 2 - px2mm(2), {
            fontSizePt: CARD_HEADER_FS, color: T.white, style: "bold",
        });
        cy += CARD_HEADER_H + CARD_PAD;

        drawSingleBracket(pdf, x + CARD_PAD, cy, draw, grade, i18n, contentWidthMm - CARD_PAD * 2);
        cy += h + CARD_PAD;

        pdf.setDrawColor(T.border);
        pdf.setLineWidth(0.15);
        pdf.roundedRect(x, cardTop, contentWidthMm, cy - cardTop, CARD_RADIUS, CARD_RADIUS, "S");
    });

    return cy;
};
